/**
 * Tenant identity hook (Plan §21 Phase 5 hard-tenancy v1).
 *
 * One fetch per page-load (cached on the module) → returns the tenant
 * identity for the TopNav + footer to render branding. Multi-tenant
 * deployments get the generic "UAIP" identity; single-tenant installs
 * return the customer's name + (optional) https logo + footer contact
 * email.
 *
 * Failure mode: if /api/tenant returns 5xx (or the network drops), we
 * fall back to the multi-tenant default rather than crash the nav. The
 * tenant identity is cosmetic — auth + crawls keep working without it.
 */

import { useEffect, useState } from "react";
import { getTenant, type TenantIdentity } from "./api";

const FALLBACK: TenantIdentity = {
  mode: "multi-tenant",
  tenantId: "shared",
  tenantName: "UAIP",
  logoUrl: "",
  contactEmail: "",
};

let cached: TenantIdentity | null = null;
let inflight: Promise<TenantIdentity> | null = null;

export function useTenant(): TenantIdentity {
  const [tenant, setTenant] = useState<TenantIdentity>(cached ?? FALLBACK);

  useEffect(() => {
    if (cached) return;
    if (!inflight) {
      inflight = getTenant()
        .then((t) => {
          cached = t;
          return t;
        })
        .catch(() => {
          cached = FALLBACK;
          return FALLBACK;
        });
    }
    let alive = true;
    inflight.then((t) => {
      if (alive) setTenant(t);
    });
    return () => {
      alive = false;
    };
  }, []);

  return tenant;
}
